import React, { memo } from 'react';
import { View, TouchableOpacity, Image, StyleSheet } from 'react-native';
import { createUseStyle } from '@redlink/assets/styles';
import { Text, IconLink } from '@redlink/components/atoms';

export const PreviewPostCard = memo(
  ({
    images = [],
    style,
    onPress,
    children,
    BodyComponent,
    compact = false,
    smallTile = false,
    hasLink = false,
    themeIndependent = false,
  }) => {
    const { styles } = useStyle();
    return (
      <TouchableOpacity
        {...{ onPress }}
        activeOpacity={0.6}
        style={[
          style,
          styles.container,
          smallTile && styles.tileContainer,
          themeIndependent && styles.panelContainer,
        ]}
      >
        <BodyContainer {...{ images, compact, smallTile, hasLink }}>
          {BodyComponent}
        </BodyContainer>
        {children}
      </TouchableOpacity>
    );
  }
);

const BodyContainer = memo(({ compact, ...rest }) => {
  return !compact ? (
    <NormalBodyContainer {...rest} />
  ) : (
    <CompactBodyContainer {...rest} />
  );
});

const NormalBodyContainer = memo(({ images, smallTile, hasLink, children }) => {
  const { styles, colors } = useStyle();
  const multiImage = images.length > 1;
  return (
    <>
      <View
        style={[styles.imageContainer, smallTile && styles.smallImageContainer]}
      >
        <View style={styles.thumbnailContainer}>
          <Image
            source={{ uri: images[0]?.thumbnailUrl }}
            style={styles.image}
          />
        </View>
        {multiImage && (
          <View
            style={[
              styles.floatedItem,
              smallTile
                ? styles.imageCountContainerSmall
                : styles.imageCountContainer,
            ]}
          >
            <Text style={styles.whiteText}>{images.length}</Text>
          </View>
        )}
        {hasLink && (
          <View style={[styles.floatedItem, styles.imageLinkContainer]}>
            <IconLink color={colors.white} size={14} />
          </View>
        )}
      </View>
      {children}
    </>
  );
});

const CompactBodyContainer = memo(({ images, children }) => {
  const { styles } = useStyle();
  return (
    <View style={styles.compactBodyContainer}>
      <View style={styles.compactImageContainer}>
        <Image source={{ uri: images[0]?.thumbnailUrl }} style={styles.image} />
      </View>
      <View style={styles.compactBodyComponentContainer}>{children}</View>
    </View>
  );
});

const useStyle = createUseStyle(({ colors, shadow, contentWidth, size }) => ({
  container: {
    backgroundColor: colors.cardBackground,
    borderRadius: 15,
    overflow: 'hidden',
  },
  tileContainer: {
    backgroundColor: colors.cardBackground,
  },
  panelContainer: {
    backgroundColor: colors.gray7,
  },
  imageContainer: {
    width: contentWidth - 2 * size.postListPaddingHorizontal,
    height: contentWidth - 2 * size.postListPaddingHorizontal,
  },
  smallImageContainer: {
    width: 192,
    height: 192,
  },
  thumbnailContainer: {
    borderRadius: 10,
    overflow: 'hidden',
    position: 'absolute',
    left: 8,
    right: 8,
    top: 8,
    bottom: 0,
  },
  image: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  floatedItem: {
    minWidth: 20,
    height: 24,
    borderRadius: 5,
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.black4,
  },
  imageCountContainer: {
    minWidth: 24,
    left: 18,
    bottom: 10,
  },
  imageCountContainerSmall: {
    minWidth: 24,
    left: 18,
    bottom: 10,
  },
  whiteText: {
    color: colors.white,
  },
  imageLinkContainer: {
    top: 18,
    width: 24,
    right: 18,
  },
  compactBodyContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  compactBodyComponentContainer: {
    flex: 1,
    marginTop: 10,
  },
  compactImageContainer: {
    height: 48,
    width: 48,
    marginStart: 8,
  },
}));
